import { useState } from 'react';
import type { UseFormReturn, FieldValues, Path } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import type { CepLookupResponse } from '@barbeiropro/contracts';
import { Input } from '@/components/ui/input';
import { api, errorMessage } from '@/lib/api';
import { digits } from '@/lib/format';
import { Field } from './field';
import { MaskedInput } from './masked-input';

/**
 * Endereço na ordem CEP → Estado → Cidade → Bairro → Rua → Nº → Complemento.
 * O CEP é consultado pelo backend (ViaCEP) ao completar 8 dígitos.
 */
export function AddressFields<T extends FieldValues>({ form, prefix = 'address' }: { form: UseFormReturn<T>; prefix?: string }) {
  const [loading, setLoading] = useState(false);
  const [cepError, setCepError] = useState<string>();
  const name = (f: string) => `${prefix}.${f}` as Path<T>;
  const err = (f: string) => {
    const e = (form.formState.errors as Record<string, Record<string, { message?: string }>>)[prefix]?.[f];
    return e?.message;
  };

  async function lookup(v: string) {
    const cep = digits(v);
    if (cep.length !== 8) return;
    setLoading(true);
    setCepError(undefined);
    try {
      const r = await api.get<CepLookupResponse>(`/org/cep/${cep}`);
      const opts = { shouldValidate: true, shouldDirty: true };
      form.setValue(name('state'), r.state as never, opts);
      form.setValue(name('city'), r.city as never, opts);
      if (r.district) form.setValue(name('district'), r.district as never, opts);
      if (r.street) form.setValue(name('street'), r.street as never, opts);
      form.setFocus(name(r.street ? 'number' : 'street'));
    } catch (e) {
      setCepError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  const reg = (f: string) => form.register(name(f));
  return (
    <div className="grid gap-3 sm:grid-cols-6">
      <Field id={`${prefix}-cep`} label="CEP" error={cepError ?? err('cep')} className="sm:col-span-2">
        <div className="relative">
          <MaskedInput mask="cep" id={`${prefix}-cep`} placeholder="00000-000" {...reg('cep')} onBlur={(e) => lookup(e.target.value)} onKeyUp={(e) => lookup((e.target as HTMLInputElement).value)} />
          {loading && <Loader2 className="absolute top-1/2 right-2 size-4 -translate-y-1/2 animate-spin text-muted-foreground" aria-label="Consultando CEP" />}
        </div>
      </Field>
      <Field id={`${prefix}-state`} label="Estado" error={err('state')} className="sm:col-span-1">
        <Input id={`${prefix}-state`} maxLength={2} className="uppercase" {...reg('state')} />
      </Field>
      <Field id={`${prefix}-city`} label="Cidade" error={err('city')} className="sm:col-span-3">
        <Input id={`${prefix}-city`} {...reg('city')} />
      </Field>
      <Field id={`${prefix}-district`} label="Bairro" error={err('district')} className="sm:col-span-2">
        <Input id={`${prefix}-district`} {...reg('district')} />
      </Field>
      <Field id={`${prefix}-street`} label="Rua" error={err('street')} className="sm:col-span-4">
        <Input id={`${prefix}-street`} {...reg('street')} />
      </Field>
      <Field id={`${prefix}-number`} label="Número" error={err('number')} className="sm:col-span-2">
        <Input id={`${prefix}-number`} {...reg('number')} />
      </Field>
      <Field id={`${prefix}-complement`} label="Complemento" error={err('complement')} className="sm:col-span-4">
        <Input id={`${prefix}-complement`} {...reg('complement')} />
      </Field>
    </div>
  );
}
